/**
 * Offer send delivery polling — classify job/delivery status without minting
 * a new request_key while status is still unknown (avoids double e-mail).
 */

export const MAILED_RECOVERY_DELIVERY_STATUSES = new Set([
  "finalization_failed",
  "draft_creation_failed",
  "sent_needs_attention",
  "needs_follow_up",
]);

/**
 * @typedef {"in_progress"|"completed"|"mailed_needs_followup"|"failed"|"status_unconfirmed"} SendPollKind
 * @typedef {{
 *   kind: SendPollKind,
 *   clearActiveJob: boolean,
 *   mintNewRequestKey: boolean,
 *   deliveryStatus?: string|null,
 *   jobStatus?: string|null,
 *   needsFollowUp?: boolean,
 * }} SendPollOutcome
 */

/**
 * Timeout / unresolved poll: keep delivery id + request_key; do not treat as failed.
 * @returns {SendPollOutcome}
 */
export function classifySendTimeout() {
  return {
    kind: "status_unconfirmed",
    clearActiveJob: false,
    mintNewRequestKey: false,
  };
}

/**
 * True when a sync send_offer envelope must not be toasted as a send failure.
 * Includes queued jobs, delivered mail, and mailed-recovery (mail left,
 * revision needs follow-up) even when job_id is absent.
 */
export function isSendOfferAccepted(response) {
  if (!response || typeof response !== "object") return false;
  if (
    response.error ||
    (Array.isArray(response.errors) && response.errors.length)
  ) {
    return false;
  }
  return Boolean(
    response.job_id ||
      response.delivery_status === "sent" ||
      isMailedRecoveryStatus(response)
  );
}

/**
 * True when status payload says commercial finalization still needs follow-up
 * (mail may already have left). FE must keep the same request_key and re-POST
 * send_offer so API 379 can re-enqueue recovery without re-mailing.
 */
export function isMailedRecoveryStatus(statusResponse) {
  if (!statusResponse || typeof statusResponse !== "object") return false;
  if (statusResponse.needs_follow_up === true) return true;
  const delivery = statusResponse.delivery_status
    ? String(statusResponse.delivery_status)
    : "";
  return MAILED_RECOVERY_DELIVERY_STATUSES.has(delivery);
}

/**
 * Classify a send_offer_status payload.
 * API maps sent / finalization_failed / draft_creation_failed → job_status
 * "completed" and sets needs_follow_up for recovery cases. Use delivery_status
 * / needs_follow_up to distinguish mailed recovery from true success.
 *
 * @param {object|null|undefined} statusResponse
 * @param {{ isHardFailure?: (payload: object) => boolean }} [opts]
 * @returns {SendPollOutcome}
 */
export function classifySendPollResult(statusResponse, opts = {}) {
  if (!statusResponse || typeof statusResponse !== "object") {
    return classifySendTimeout();
  }

  const isHardFailure =
    typeof opts.isHardFailure === "function"
      ? opts.isHardFailure
      : () => false;

  if (isHardFailure(statusResponse)) {
    return {
      kind: "failed",
      clearActiveJob: true,
      mintNewRequestKey: true,
      deliveryStatus: statusResponse.delivery_status || null,
      jobStatus: statusResponse.job_status || null,
    };
  }

  const delivery = statusResponse.delivery_status
    ? String(statusResponse.delivery_status)
    : null;
  const job = statusResponse.job_status
    ? String(statusResponse.job_status)
    : null;

  if (isMailedRecoveryStatus(statusResponse)) {
    return {
      kind: "mailed_needs_followup",
      // Keep request_key so "Fullfør revisjon" can re-POST the same key.
      // Job id may be cleared; recovery re-POST returns the same delivery.
      clearActiveJob: false,
      mintNewRequestKey: false,
      deliveryStatus: delivery,
      jobStatus: job,
      needsFollowUp: true,
    };
  }

  if (job === "completed" || delivery === "sent") {
    return {
      kind: "completed",
      clearActiveJob: true,
      mintNewRequestKey: true,
      deliveryStatus: delivery,
      jobStatus: job,
    };
  }

  if (job === "failed" || delivery === "failed") {
    return {
      kind: "failed",
      clearActiveJob: true,
      mintNewRequestKey: true,
      deliveryStatus: delivery,
      jobStatus: job,
    };
  }

  if (
    job === "processing" ||
    job === "queued" ||
    delivery === "processing" ||
    delivery === "queued"
  ) {
    return {
      kind: "in_progress",
      clearActiveJob: false,
      mintNewRequestKey: false,
      deliveryStatus: delivery,
      jobStatus: job,
    };
  }

  // Missing or unexpected status — do not mint a new delivery key.
  return {
    kind: "status_unconfirmed",
    clearActiveJob: false,
    mintNewRequestKey: false,
    deliveryStatus: delivery,
    jobStatus: job,
  };
}

/**
 * True when a later action must reuse the same request_key + active job
 * (status check / poll only — not mailed recovery re-POST).
 */
export function shouldReuseDeliveryAttempt({
  activeJob,
  requestKey,
  jobStatus,
} = {}) {
  if (!requestKey || !activeJob?.id) return false;
  return (
    jobStatus === "status_unconfirmed" ||
    jobStatus === "timeout" ||
    jobStatus === "queued" ||
    jobStatus === "processing"
  );
}

/**
 * True when UI should re-POST send_offer with the same request_key to finish
 * commercial finalization (API re-enqueues without re-mailing).
 */
export function shouldResumeMailedRecovery({ requestKey, jobStatus } = {}) {
  return Boolean(requestKey) && jobStatus === "mailed_needs_followup";
}

/**
 * Contract for OfferSendDialog primary action:
 * - timeout / in-flight → poll only (same request_key)
 * - needs_follow_up / «Fullfør revisjon» → re-POST send_offer with SAME key
 * - never mint a new request_key on timeout or recovery
 *
 * @returns {{
 *   action: "poll"|"repost_same_key"|"fresh_send",
 *   requestKey: string|null,
 *   mintNewRequestKey: boolean,
 * }}
 */
export function resolveSendOfferAttempt({
  activeJob,
  requestKey,
  jobStatus,
} = {}) {
  const key = requestKey || null;
  if (
    shouldReuseDeliveryAttempt({
      activeJob,
      requestKey: key,
      jobStatus,
    })
  ) {
    return {
      action: "poll",
      requestKey: key,
      mintNewRequestKey: false,
    };
  }
  if (shouldResumeMailedRecovery({ requestKey: key, jobStatus })) {
    return {
      action: "repost_same_key",
      requestKey: key,
      mintNewRequestKey: false,
    };
  }
  return {
    action: "fresh_send",
    requestKey: key,
    mintNewRequestKey: !key,
  };
}

const SEND_RECOVERY_STORAGE_PREFIX = "promang.offer.send.recovery.v1.";

/**
 * Persist mailed-recovery keys so «Fullfør revisjon» survives hard reload.
 * In-memory Map covers close/reopen in the same session; sessionStorage covers
 * hard reload.
 *
 * @param {string|number|null|undefined} offerId
 * @returns {{ requestKey: string, jobId?: string|null }|null}
 */
export function loadSendRecovery(offerId) {
  if (offerId == null || typeof sessionStorage === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(
      `${SEND_RECOVERY_STORAGE_PREFIX}${offerId}`
    );
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.requestKey) return null;
    return {
      requestKey: String(parsed.requestKey),
      jobId: parsed.jobId != null ? String(parsed.jobId) : null,
    };
  } catch {
    return null;
  }
}

/**
 * @param {string|number|null|undefined} offerId
 * @param {{ requestKey: string, jobId?: string|null }|null|undefined} recovery
 */
export function saveSendRecovery(offerId, recovery) {
  if (offerId == null || typeof sessionStorage === "undefined") return;
  try {
    if (!recovery?.requestKey) {
      sessionStorage.removeItem(`${SEND_RECOVERY_STORAGE_PREFIX}${offerId}`);
      return;
    }
    sessionStorage.setItem(
      `${SEND_RECOVERY_STORAGE_PREFIX}${offerId}`,
      JSON.stringify({
        requestKey: String(recovery.requestKey),
        jobId: recovery.jobId != null ? String(recovery.jobId) : null,
      })
    );
  } catch {
    // Quota / private mode — in-memory Map still covers close/reopen.
  }
}

/** @param {string|number|null|undefined} offerId */
export function clearSendRecovery(offerId) {
  saveSendRecovery(offerId, null);
}

/**
 * True when a sync send_offer envelope is terminal success (mail + revision done).
 * Job-less `delivery_status: "sent"` must clear sticky recovery so reopen does
 * not show «Fullfør revisjon» after finalization already succeeded.
 * Mailed-recovery statuses must NOT clear — request_key is still needed.
 */
export function isTerminalSendSuccess(response) {
  if (!response || typeof response !== "object") return false;
  if (isMailedRecoveryStatus(response)) return false;
  if (
    response.error ||
    (Array.isArray(response.errors) && response.errors.length)
  ) {
    return false;
  }
  return response.delivery_status === "sent";
}

/**
 * Clear sticky send recovery after terminal success (sync or poll).
 * No-op for mailed recovery / in-flight / failures.
 *
 * @param {string|number|null|undefined} offerId
 * @param {object|null|undefined} response
 * @param {{ deleteMemory?: (id: string|number) => void }} [opts]
 * @returns {boolean} true when recovery was cleared
 */
export function clearSendRecoveryIfTerminalSuccess(offerId, response, opts = {}) {
  if (offerId == null || !isTerminalSendSuccess(response)) return false;
  if (typeof opts.deleteMemory === "function") {
    opts.deleteMemory(offerId);
  }
  clearSendRecovery(offerId);
  return true;
}

/** Toast / label key for mailed recovery delivery_status. */
export function mailedRecoveryMessageKey(deliveryStatus) {
  if (deliveryStatus === "finalization_failed") {
    return "offers.workspace.delivery_finalization_failed";
  }
  if (deliveryStatus === "draft_creation_failed") {
    return "offers.workspace.delivery_draft_creation_failed";
  }
  if (
    deliveryStatus === "sent_needs_attention" ||
    deliveryStatus === "needs_follow_up"
  ) {
    return "offers.send.sent_needs_attention";
  }
  return "offers.send.mailed_needs_followup";
}
